<script setup lang="ts">
import {
  discardFailed,
  isMine as isMineIn,
  markRead,
  mySidesIn,
  answerInvitation,
  sendMessage,
  threadMessages,
  threadAvatar,
  threadPicture,
  threadTitle,
  uploadAttachment,
  useMessaging,
  type ShownMessage,
  type Thread,
} from "../composables/useMessaging"
import { ApiError, NetworkError } from "../lib/api"
import { ACCEPTED_FILES, MAX_FILE_BYTES, shrinkPicture } from "../lib/files"
import { dateLocale, t, tn } from "../lib/i18n"
import type { Attachment } from "../lib/localdb"
import { uuid } from "../lib/uuid"

// One conversation. Rich content: the message-body slot gets { message, thread } and shows the
// message instead of its text, e.g. the POS drawing an order card.
const props = defineProps<{
  thread: Thread
  hideBack?: boolean
  /** Shown beside the order (the POS's order view), which already shows what the header and lines would. */
  orderView?: boolean
  /** "Open order" on an order conversation: emits open-order with the order id. */
  showOpenOrder?: boolean
}>()
const emit = defineEmits<{ close: []; "open-order": [orderId: string] }>()

const hub = useMessaging()
const draft = ref("")
const scroller = ref<HTMLElement | null>(null)
const sides = computed(() => mySidesIn(props.thread))

/** Between two shops: invitations, and sales channel offers from an administrator's shop */
const betweenShops = computed(
  () => props.thread.kind === "organisations" && props.thread.organisations.length === 2 && props.thread.organisations.every((o) => o.id.startsWith("shop:")),
)
const invitedName = computed(() => props.thread.organisations.find((o) => o.id === props.thread.invitedOrganisationId)?.name ?? "")
const invitedMe = computed(() => !!props.thread.invitedOrganisationId && sides.value.some((o) => o.id === props.thread.invitedOrganisationId))
const otherShop = computed(() => props.thread.organisations.find((o) => !sides.value.some((s) => s.id === o.id))?.name ?? "")
const offeringSide = computed(() => (betweenShops.value && !props.thread.invitedOrganisationId && !props.thread.declined ? sides.value.find((o) => o.role === "admin") : undefined))
const offering = ref(false)
const answering = ref(false)

async function answer(accept: boolean) {
  answering.value = true
  try {
    await answerInvitation(props.thread.id, accept)
  } finally {
    answering.value = false
  }
}
const speakAs = ref(sides.value[0]?.id ?? "")
const messages = computed(() => threadMessages(props.thread.id))
const inOrderView = computed(() => !!props.orderView)
const avatar = computed(() => threadAvatar(props.thread, 40))
/** An order's picture (its product's) in place of the avatar */
const picture = computed(() => threadPicture(props.thread, 40))
const pictureFailed = ref(false)

function isMine(m: ShownMessage) {
  return isMineIn(props.thread, m)
}

function author(m: ShownMessage): string {
  if (isMine(m)) return m.authorOrganisationName && !props.thread.asCustomer ? `${t("You")} · ${m.authorOrganisationName}` : t("You")
  // Your own order: the shop's messages carry its name
  if (props.thread.asCustomer) return m.authorName || t("Someone")
  if (m.authorCustomer) return `${m.authorName || t("Customer")} · ${t("customer")}`
  const name = m.authorName || t("Someone")
  return m.authorOrganisationName ? `${name} · ${m.authorOrganisationName}` : name
}

function dayKey(iso: string) {
  return new Date(iso).toDateString()
}

function dayLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(Date.now() - 24 * 3600 * 1000)
  if (d.toDateString() === today.toDateString()) return t("Today")
  if (d.toDateString() === yesterday.toDateString()) return t("Yesterday")
  return d.toLocaleDateString(dateLocale(), { weekday: "short", day: "numeric", month: "numeric" })
}

/** Messages with a day line before each new day, and the author shown when it changes. */
const shown = computed(() =>
  messages.value.map((m, i) => {
    const prev = messages.value[i - 1]
    const newDay = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt)
    const sameAuthor =
      !newDay &&
      !!prev &&
      prev.authorPersonId === m.authorPersonId &&
      !!prev.authorCustomer === !!m.authorCustomer &&
      new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 10 * 60 * 1000
    return { m, newDay, showAuthor: !sameAuthor }
  }),
)

/** For order threads: what was ordered, and when. */
const orderLines = computed(() =>
  (props.thread.orderContext?.items ?? []).map((item) => {
    const when = [item.start, item.end].filter(Boolean).map((v) => formatWhen(v!)).join(" – ")
    return `${item.count > 1 ? `${item.count} × ` : ""}${item.name}${when ? ` (${when})` : ""}`
  }),
)

function formatWhen(value: string): string {
  // ISO times from start_ts; plain dates as Johku gave them
  if (!value.includes("T")) return value
  return new Date(value).toLocaleString(dateLocale(), { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })
}

const subtitle = computed(() => {
  const order = props.thread.orderContext
  if (props.thread.kind === "order" && order) {
    const date = order.orderDate ? new Date(order.orderDate * 1000).toLocaleDateString(dateLocale()) : ""
    const who = props.thread.asCustomer ? props.thread.organisations[0]?.name : t("With the customer")
    const labels: Record<string, string> = { cancelled: t("Cancelled"), refunded: t("Refunded"), unpaid: t("Unpaid") }
    const status = labels[order.status ?? ""] ?? ""
    return [who, date, status].filter(Boolean).join(" · ")
  }
  const group = props.thread.groupContext
  if (props.thread.kind === "group" && group) {
    return group.sent < group.recipients
      ? t("To the members of {collection}: sending, {sent}/{n}", { collection: group.collection, sent: group.sent, n: group.recipients })
      : tn(group.recipients, "To the members of {collection}: {n} member", "To the members of {collection}: {n} members", { collection: group.collection })
  }
  const member = props.thread.memberContext
  if (props.thread.kind === "member" && member) {
    return props.thread.asCustomer ? member.collection : [member.name ? member.email : "", member.collection].filter(Boolean).join(" · ")
  }
  return [...props.thread.organisations.map((o) => o.name), ...props.thread.people.map((p) => p.name || p.email)].join(", ")
})

function time(iso: string) {
  return new Date(iso).toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" })
}

async function scrollToEnd() {
  await nextTick()
  if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight
}

/** Files picked for the next message: uploading, ready, or refused */
interface PendingFile {
  key: string
  name: string
  progress: number
  attachment?: Attachment
  error?: string
}
const MAX_FILES = 10
const pending = ref<PendingFile[]>([])
const fileInput = ref<HTMLInputElement | null>(null)
const uploading = computed(() => pending.value.some((p) => !p.attachment && !p.error))
const ready = computed(() => pending.value.flatMap((p) => (p.attachment ? [p.attachment] : [])))
const canSend = computed(() => !uploading.value && (!!draft.value.trim() || ready.value.length > 0))

function uploadError(err: unknown): string {
  if (err instanceof NetworkError) return t("Offline")
  if (err instanceof ApiError && err.code === "file_too_large") return t("The file is larger than 20 MB")
  if (err instanceof ApiError && err.code === "file_type_not_allowed") return t("Pictures, PDF, text, Word and Excel files only")
  return err instanceof Error ? err.message : String(err)
}

async function addFiles(files: File[]) {
  for (const file of files.slice(0, Math.max(0, MAX_FILES - pending.value.length))) {
    pending.value.push({ key: uuid(), name: file.name, progress: 0 })
    // The array's own (reactive) copy, so progress shows
    const item = pending.value.at(-1)!
    void (async () => {
      try {
        const { blob, name } = await shrinkPicture(file)
        item.name = name
        if (blob.size > MAX_FILE_BYTES) throw new ApiError(413, "file_too_large", "")
        item.attachment = await uploadAttachment(props.thread.id, blob, name, (f) => (item.progress = f))
      } catch (err) {
        item.error = uploadError(err)
      }
    })()
  }
}

function picked(event: Event) {
  const input = event.target as HTMLInputElement
  void addFiles([...(input.files ?? [])])
  input.value = ""
}

/** A picture pasted into the message, e.g. a screenshot in the POS */
function pasted(event: ClipboardEvent) {
  const files = [...(event.clipboardData?.files ?? [])]
  if (!files.length) return
  event.preventDefault()
  void addFiles(files)
}

const dragging = ref(false)
function dropped(event: DragEvent) {
  dragging.value = false
  const files = [...(event.dataTransfer?.files ?? [])]
  if (files.length) void addFiles(files)
}

function removePending(key: string) {
  // An upload no message uses is removed on the server after a day
  pending.value = pending.value.filter((p) => p.key !== key)
}

async function send() {
  if (!canSend.value) return
  const body = draft.value
  const files = ready.value
  draft.value = ""
  pending.value = []
  await sendMessage(props.thread.id, body, sides.value.length > 1 ? speakAs.value : undefined, files)
  void scrollToEnd()
}

function onKeydown(event: KeyboardEvent) {
  // Enter sends on wide screens with a keyboard; on phones Enter makes a new line and the button sends.
  if (event.key === "Enter" && !event.shiftKey && window.matchMedia("(min-width: 600px) and (pointer: fine)").matches) {
    event.preventDefault()
    void send()
  }
}

/** A search hit: shown in the middle, outlined for a moment */
const focused = ref<string | null>(null)
async function showFocused(): Promise<boolean> {
  const id = hub.focusMessageId
  if (!id || !messages.value.some((m) => m.id === id)) return false
  hub.focusMessageId = null
  await nextTick()
  scroller.value?.querySelector(`[data-message-id="${id}"]`)?.scrollIntoView({ block: "center" })
  focused.value = id
  setTimeout(() => {
    if (focused.value === id) focused.value = null
  }, 2500)
  return true
}

watch(
  () => messages.value.length,
  async () => {
    if (!(await showFocused())) void scrollToEnd()
    void markRead(props.thread.id)
  },
  { immediate: true },
)
watch(() => hub.focusMessageId, () => void showFocused())
</script>

<template>
  <div
    class="thread"
    :class="{ dragging }"
    @dragover.prevent="dragging = true"
    @dragleave.self="dragging = false"
    @drop.prevent="dropped"
  >
    <header v-if="!inOrderView" class="bar">
      <button v-if="!hideBack" class="back hub-icon-button" type="button" :aria-label="t('Back')" @click="emit('close')">
        <HubIcon name="back" :size="22" />
      </button>
      <img v-if="picture && !pictureFailed" class="picture" :src="picture" alt="" @error="pictureFailed = true" />
      <HubAvatar v-else :name="avatar.name" :id="avatar.id" :src="avatar.photo ? null : avatar.image" :size="40" />
      <div class="heading">
        <strong>{{ threadTitle(thread) }}</strong>
        <span>{{ subtitle }}</span>
      </div>
      <button
        v-if="showOpenOrder && thread.kind === 'order' && thread.orderContext"
        class="hub-btn hub-btn-quiet open-order"
        type="button"
        @click="emit('open-order', thread.orderContext.orderId)"
      >
        {{ t("Open the order") }}
      </button>
      <button v-if="offeringSide" class="hub-btn hub-btn-quiet open-order" type="button" @click="offering = true">
        {{ t("Make an offer") }}
      </button>
    </header>
    <div v-if="thread.invitedOrganisationId" class="invitation" role="status">
      <template v-if="invitedMe">
        <span>{{ t("{shop} would like to talk with you. Reply or accept to continue.", { shop: otherShop }) }}</span>
        <span class="invitation-buttons">
          <button class="hub-btn" type="button" :disabled="answering" @click="answer(true)">{{ t("Accept") }}</button>
          <button class="hub-btn hub-btn-quiet" type="button" :disabled="answering" @click="answer(false)">{{ t("Decline") }}</button>
        </span>
      </template>
      <span v-else>{{ t("Waiting for {shop} to accept the conversation.", { shop: invitedName }) }}</span>
    </div>
    <HubOfferSheet v-if="offering && offeringSide" :thread="thread" :as-organisation-id="offeringSide.id" @close="offering = false" />
    <ul v-if="orderLines.length && !inOrderView" class="order-lines">
      <li v-for="(line, i) in orderLines" :key="i">{{ line }}</li>
    </ul>

    <div ref="scroller" class="messages">
      <p v-if="messages.length === 0" class="empty">{{ t("No messages yet.") }}</p>
      <template v-for="{ m, newDay, showAuthor } in shown" :key="m.id">
        <p v-if="newDay" class="day">{{ dayLabel(m.createdAt) }}</p>
        <article
          class="message"
          :class="{ mine: isMine(m), failed: m.status === 'failed', grouped: !showAuthor, focused: focused === m.id }"
          :data-message-id="m.id"
        >
          <div v-if="showAuthor" class="meta">{{ author(m) }}</div>
          <HubFiles v-if="m.attachments?.length" :files="m.attachments" :sending="m.status !== 'delivered'" />
          <HubProposal v-if="m.kind === 'proposal'" :message-id="m.id" :sending="m.status !== 'delivered'" :version="messages.length" />
          <div v-if="m.body" class="body"><slot name="message-body" :message="m" :thread="thread">{{ m.body }}</slot></div>
          <div class="foot">
            <span v-if="m.status === 'sending'">{{ t("Sending…") }}</span>
            <span v-else-if="m.status === 'failed'">
              {{ t("Not sent: {error}", { error: m.error ?? "" }) }}
              <button type="button" class="link" @click="discardFailed(m.id)">{{ t("Remove") }}</button>
            </span>
            <span v-else>{{ time(m.createdAt) }}</span>
          </div>
        </article>
      </template>
    </div>

    <p v-if="thread.kind === 'group'" class="group-note">{{ t("Members' replies come as conversations of their own.") }}</p>
    <p v-else-if="thread.declined" class="group-note">{{ t("The conversation was declined.") }}</p>
    <form v-else class="composer" @submit.prevent="send">
      <select v-if="sides.length > 1" v-model="speakAs" class="hub-field side" :aria-label="t('Send as')">
        <option v-for="s in sides" :key="s.id" :value="s.id">{{ t("As {name}", { name: s.name }) }}</option>
      </select>
      <ul v-if="pending.length" class="pending">
        <li v-for="p in pending" :key="p.key" :class="{ refused: p.error }">
          <HubIcon name="file" :size="18" />
          <span class="pending-name">{{ p.name }}</span>
          <span v-if="p.error" class="pending-state">{{ p.error }}</span>
          <span v-else-if="!p.attachment" class="pending-state">{{ Math.round(p.progress * 100) }} %</span>
          <button class="hub-icon-button" type="button" :aria-label="t('Remove')" @click="removePending(p.key)">
            <HubIcon name="close" :size="18" />
          </button>
          <span v-if="!p.attachment && !p.error" class="progress" :style="{ width: `${p.progress * 100}%` }" />
        </li>
      </ul>
      <div class="row">
        <button
          class="hub-icon-button attach"
          type="button"
          :disabled="pending.length >= MAX_FILES"
          :aria-label="t('Attach a file')"
          :title="t('Attach a file')"
          @click="fileInput?.click()"
        >
          <HubIcon name="attach" :size="22" />
        </button>
        <input ref="fileInput" type="file" multiple :accept="ACCEPTED_FILES" hidden @change="picked" />
        <textarea
          v-model="draft"
          class="hub-field"
          rows="1"
          :placeholder="thread.kind === 'order' ? (thread.asCustomer ? t('Write to the shop') : t('Write to the customer')) : t('Message')"
          :aria-label="t('Message')"
          @keydown="onKeydown"
          @paste="pasted"
        />
        <button class="hub-btn send" type="submit" :disabled="!canSend" :aria-label="t('Send')">
          <HubIcon name="send" :size="20" filled /><span class="send-label">{{ t("Send") }}</span>
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.thread {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: max(10px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) 10px max(10px, env(safe-area-inset-left));
  background: var(--hub-surface);
  border-bottom: 1px solid var(--hub-border);
}

.back {
  margin-left: -6px;
}

.picture {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: cover;
}

/* Beside the list in a wide inbox (HubInbox's container): no way back needed */
@container hub-inbox (min-width: 600px) {
  .back {
    display: none;
  }
  .bar {
    padding-left: 16px;
  }
}

.heading {
  display: grid;
  min-width: 0;
  flex: 1;
}

.heading strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--hub-heading);
  font-family: var(--hub-font-display);
  font-size: 1.06rem;
}

.heading span {
  color: var(--hub-muted);
  font-size: 0.82rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.open-order {
  min-height: 34px;
  padding: 6px 12px;
  font-size: 0.85rem;
}

.order-lines {
  margin: 0;
  padding: 0 16px 10px 66px;
  list-style: none;
  background: var(--hub-surface);
  border-bottom: 1px solid var(--hub-border);
  color: var(--hub-muted);
  font-size: 0.85rem;
}

.bar:has(+ .order-lines) {
  border-bottom: 0;
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px max(12px, env(safe-area-inset-right)) 16px max(12px, env(safe-area-inset-left));
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.day {
  align-self: center;
  margin: 10px 0 4px;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--hub-tint);
  color: var(--hub-muted);
  font-size: 0.75rem;
  font-weight: 600;
}

.message {
  max-width: min(80%, 560px);
  align-self: flex-start;
  background: var(--hub-theirs);
  border-radius: var(--hub-radius-bubble) var(--hub-radius-bubble) var(--hub-radius-bubble) var(--hub-radius-tail);
  padding: 8px 12px 6px;
  margin-top: 6px;
  box-shadow: 0 1px 0 rgb(0 0 0 / 0.05);
}

.message.grouped {
  margin-top: 0;
}

.message.mine {
  align-self: flex-end;
  background: var(--hub-mine);
  border-radius: var(--hub-radius-bubble) var(--hub-radius-bubble) var(--hub-radius-tail) var(--hub-radius-bubble);
}

.message.failed {
  outline: 1px solid var(--hub-danger);
}

.message.focused {
  outline: 2px solid var(--hub-brand);
  outline-offset: 2px;
}

.meta {
  color: var(--hub-muted);
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 1px;
}

.body {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.foot {
  color: var(--hub-muted);
  font-size: 0.7rem;
  text-align: right;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.failed .foot {
  color: var(--hub-danger);
  text-align: left;
}

.link {
  background: none;
  border: 0;
  color: inherit;
  text-decoration: underline;
  padding: 0 4px;
}

.empty {
  color: var(--hub-muted);
  margin: auto;
}

.composer {
  display: grid;
  gap: 8px;
  padding: 10px max(12px, env(safe-area-inset-right)) max(10px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
  background: var(--hub-surface);
  border-top: 1px solid var(--hub-border);
}

.row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

textarea {
  flex: 1;
  resize: none;
  max-height: 40vh;
  field-sizing: content;
  min-height: 44px;
  border-radius: 22px;
  border-color: transparent;
  padding: 11px 16px;
}

/* Sending is the one place, besides the bar's active tab, with Johku's yellow */
.send {
  width: 44px;
  height: 44px;
  min-height: 44px;
  padding: 0;
  border-radius: 50%;
  background: var(--hub-ind);
  color: var(--hub-on-ind);
}

.send:disabled {
  opacity: 1;
  background: var(--hub-tint);
  color: var(--hub-muted);
}

.send-label {
  display: none;
}

.side {
  padding: 8px 12px;
}

.invitation {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 10px 16px;
  background: var(--hub-warning-bg);
  font-size: 0.9rem;
}

.invitation-buttons {
  display: flex;
  gap: 8px;
}

.invitation .hub-btn {
  min-height: 34px;
  padding: 6px 14px;
}

.group-note {
  margin: 0;
  padding: 12px max(16px, env(safe-area-inset-right)) max(12px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
  background: var(--hub-surface);
  border-top: 1px solid var(--hub-border);
  color: var(--hub-muted);
  font-size: 0.9rem;
  text-align: center;
}

.thread.dragging .composer {
  outline: 2px dashed var(--hub-brand);
  outline-offset: -4px;
}

.attach {
  flex: none;
  width: 40px;
  height: 44px;
  color: var(--hub-muted);
}

.pending {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pending li {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 2px 4px 10px;
  border-radius: 10px;
  background: var(--hub-tint);
  font-size: 0.85rem;
  overflow: hidden;
}

.pending li.refused {
  color: var(--hub-danger);
}

.pending-name {
  min-width: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.pending-state {
  color: var(--hub-muted);
  font-variant-numeric: tabular-nums;
}

.refused .pending-state {
  color: inherit;
}

.pending .hub-icon-button {
  width: 30px;
  height: 30px;
  min-height: 30px;
}

.progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  background: var(--hub-brand);
  transition: width 0.2s;
}

/* Inside the POS: square bubbles with a border, the green Send with its label */
.hub-theme-pos .message {
  border: 1px solid var(--hub-border);
}

.hub-theme-pos .message.mine {
  border-color: var(--hub-mine);
}

.hub-theme-pos .day {
  background: none;
}

.hub-theme-pos .meta {
  font-weight: 400;
  color: var(--hub-muted);
}

.hub-theme-pos textarea {
  border-radius: 0;
}

.hub-theme-pos .send {
  width: auto;
  padding: 0 16px;
  border-radius: 0;
  background: var(--hub-brand);
  color: var(--hub-on-brand);
}

.hub-theme-pos .send:disabled {
  opacity: 0.5;
}

.hub-theme-pos textarea {
  border-color: var(--hub-border);
  padding: 10px 14px;
}

.hub-theme-pos .message {
  box-shadow: none;
}

.hub-theme-pos .picture {
  border-radius: 2px;
}

.hub-theme-pos .send-label {
  display: inline;
}

.hub-theme-pos .composer {
  background: #fafbfb;
  border-top-color: #eeeeee;
}

.hub-theme-pos .bar .back {
  border-radius: 0;
}
</style>
